var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ClearUpgradeUxVariant, FeatureFlag } from "healthex-common/features.dtos";
import { LoginSource } from "healthex-common/heap.dtos";
import { computed, observable } from "mobx";
import { Disposer } from "../Infra/Disposer";
import { getObservableT } from "../Infra/i18next";
import { ClearAuthState } from "../Infra/Persistence";
import { ClearInitialScreen } from "./ClearAuthManager";
import { isPatientConsentRoute } from "./PatientConsentRoute";
import { PatientConsentSessionState } from "./PatientConsentSession";
import { determineOrgId, PatientConsentViewModel } from "./PatientConsentViewModel";
const t = getObservableT("patients");
const ClearUpgradeAnalyticsEvent = {
    PAGE_VIEWED: "ClearUpgradePageViewed",
    VERIFY_WITH_CLEAR_CLICKED: "ClearUpgradeVerifyWithClearClicked",
    SEARCH_MANUALLY_CLICKED: "ClearUpgradeSearchManuallyClicked",
};
export class PatientConsentClearUpgradeViewModel extends PatientConsentViewModel {
    constructor(container) {
        super(container);
        this.disposer = new Disposer();
        this.isSandboxLogin = false;
        this.hasClearError = false;
        this.isCLEARInformationShown = false;
        this.hasTrackedPageView = false;
        this.featureManager = container.featureManager;
        this.clearAuthManager = container.clearAuthManager;
        this.loginUIManager = container.loginUIManager;
        this.persistence = container.persistence;
        this.socialLoginAuthManager = container.socialLoginAuthManager;
        this.isSandboxLogin = this.persistence.isSandboxLogin();
    }
    run() {
        this.disposer.reaction(() => {
            var _a;
            return [
                this.viewToDisplay,
                this.patientConsentUIManager.session.patientConsentSessionState,
                this.featureManager.hasLoadedFeaturesForStudy((_a = this.patientConsentUIManager.session.studyId) !== null && _a !== void 0 ? _a : null),
            ];
        }, ([, sessionState, featuresLoadedForStudy]) => {
            if (sessionState === PatientConsentSessionState.RESTORING_COMPLETED) {
                this.handleClearLoginResultIfNeeded();
            }
            const view = this.viewToDisplay;
            if (view !== "LOADING" &&
                view !== "ACCOUNT_LINKING_CONFLICT" &&
                sessionState !== PatientConsentSessionState.RESTORING &&
                featuresLoadedForStudy &&
                !this.hasTrackedPageView) {
                this.hasTrackedPageView = true;
                this.trackClearUpgradeHeapEvent(ClearUpgradeAnalyticsEvent.PAGE_VIEWED, { screen: view });
            }
        }, { fireImmediately: true, name: "PatientConsentClearUpgradeViewModel_pageViewed" });
    }
    stop() {
        this.disposer.dispose();
    }
    get clearUpgradeUxVariant() {
        var _a;
        if (!this.featureManager.isFeatureEnabled(FeatureFlag.RO_CLEAR_UPGRADE_UX)) {
            return ClearUpgradeUxVariant.MANUAL_FORWARD;
        }
        const config = (_a = this.featureManager.getFeature(FeatureFlag.RO_CLEAR_UPGRADE_UX)) === null || _a === void 0 ? void 0 : _a.decodeConfiguration();
        return config !== null && config !== void 0 ? config : ClearUpgradeUxVariant.MANUAL_FORWARD;
    }
    get viewToDisplay() {
        var _a;
        if (this.hasClearError)
            return "CLEAR_ERROR";
        if (this.authManager.accountLinkingConflict)
            return "ACCOUNT_LINKING_CONFLICT";
        const isIAL2Expired = (_a = this.patientConsentUIManager.getIAL2Info()) === null || _a === void 0 ? void 0 : _a.ial2Expired;
        if (isIAL2Expired === true)
            return "CLEAR_EXPIRED";
        if (isIAL2Expired === null)
            return "LOADING";
        if (this.clearUpgradeUxVariant === ClearUpgradeUxVariant.LEGACY_CLEAR_FORWARD) {
            return "LEGACY_CLEAR_FORWARD";
        }
        return "RECORD_CHOICE_CARDS";
    }
    get flipLongPressLoginButton() {
        return this.isSandboxLogin;
    }
    get sharedWithName() {
        if (this.isIAS) {
            return null;
        }
        else if (this.isAIFlow) {
            return this.aiToolName;
        }
        else {
            return this.organizationName;
        }
    }
    get patientEmail() {
        return this.authManager.patientEmail;
    }
    get isLoggingIn() {
        return this.clearAuthManager.state === ClearAuthState.IN_PROGRESS;
    }
    get isClearLoginSuccess() {
        return this.clearAuthManager.state === ClearAuthState.SUCCESS;
    }
    get verifyWithClearButtonText() {
        switch (this.viewToDisplay) {
            case "CLEAR_EXPIRED":
                return t("Re-verify with CLEAR");
            case "CLEAR_ERROR":
                return t("Try again with CLEAR");
            default:
                return t("Verify with CLEAR");
        }
    }
    get verifyWithClearLongPressLabel() {
        return `${this.verifyWithClearButtonText} (${t("Test Patient")})`;
    }
    get searchManuallyButtonText() {
        switch (this.viewToDisplay) {
            case "LEGACY_CLEAR_FORWARD":
                return t("Search manually instead");
            case "RECORD_CHOICE_CARDS":
                return t("Search for providers");
            default:
                return t("I'll find my records myself");
        }
    }
    async verifyWithClear(isSandbox) {
        if (this.isScreenshotMode)
            return;
        this.trackClearUpgradeHeapEvent(ClearUpgradeAnalyticsEvent.VERIFY_WITH_CLEAR_CLICKED, {
            buttonText: this.verifyWithClearButtonText,
        });
        this.patientConsentUIManager.setHasLoggedInDuringConsent();
        this.persistence.setSandboxLogin(isSandbox);
        await this.patientConsentUIManager.saveConsentFlowProgress({ clearLoginAttempted: true });
        await this.patientConsentUIManager.loginToClearAndSaveState(window.location.hash, isSandbox, LoginSource.CONSENT_CLEAR_UPGRADE, determineOrgId(this.loginUIManager, this.studyInfo), ClearInitialScreen.PATIENT_CONSENT_CLEAR_UPGRADE, this.clearUpgradeUxVariant);
    }
    async findRecordsMyself() {
        if (!this.isScreenshotMode) {
            this.trackClearUpgradeHeapEvent(ClearUpgradeAnalyticsEvent.SEARCH_MANUALLY_CLICKED, {
                buttonText: this.searchManuallyButtonText,
            });
        }
        this.clearAuthManager.resetAuth();
        this.goToNextPage();
    }
    showCLEARInformation() {
        this.isCLEARInformationShown = true;
    }
    hideCLEARInformation() {
        this.isCLEARInformationShown = false;
    }
    handleClearLoginResultIfNeeded() {
        var _a;
        if (this.authManager.accountLinkingConflict) {
            this.patientConsentUIManager.completeStateRestoration();
            return;
        }
        if (this.clearAuthManager.state === ClearAuthState.EXITED) {
            this.clearAuthManager.resetAuth();
            this.patientConsentUIManager.completeStateRestoration();
            return;
        }
        const ial2Authorized = (_a = this.patientConsentUIManager.getIAL2Info()) === null || _a === void 0 ? void 0 : _a.ial2Authorized;
        if (ial2Authorized === true && this.isClearLoginSuccess) {
            const marker = this.persistence.getPendingSocialLoginSuccessMarker();
            if (marker && (this.patientConsentUIManager.hasCompleteDemographicsFromClear || this.authManager.phoneVerified)) {
                void this.analyticsService.trackSocialLoginSuccess({
                    provider: marker.provider,
                    heapGlobalEventProperties: marker.heapGlobalEventProperties,
                    timestamp: marker.timestamp,
                });
                this.persistence.setPendingSocialLoginSuccessMarker(null);
            }
            if ((this.patientConsentUIManager.hasCompleteDemographicsFromClear || this.authManager.phoneVerified) &&
                this.socialLoginAuthManager.hasPendingSocialLogin) {
                this.socialLoginAuthManager.clearPendingSocialLogin();
                this.persistence.setConsentDeferredVerificationStash(null);
            }
            const targetPageRoute = this.resolveTargetPageRoute();
            if (targetPageRoute && this.authManager.accountLinkingConflict === null) {
                this.goToNextPageAndResetAuth(targetPageRoute);
            }
        }
        else {
            this.hasClearError = true;
        }
        this.patientConsentUIManager.completeStateRestoration();
    }
    resolveTargetPageRoute() {
        var _a, _b;
        const savedNext = (_b = (_a = this.clearAuthManager.getClearAuthData()) === null || _a === void 0 ? void 0 : _a.nextPageRoute) !== null && _b !== void 0 ? _b : null;
        if (savedNext && isPatientConsentRoute(savedNext) && this.patientConsentUIManager.isRouteInPages(savedNext)) {
            return savedNext;
        }
        return this.patientConsentUIManager.getResumePageAfterClearLogin();
    }
    goToNextPageAndResetAuth(nextPageRoute) {
        if (nextPageRoute) {
            this.goToPage(nextPageRoute);
            this.clearAuthManager.resetAuth();
        }
    }
    trackClearUpgradeHeapEvent(event, extraProperties = {}) {
        if (navigator.webdriver)
            return;
        const { identity, user_id, properties } = this.getCommonHeapAttributes();
        void this.analyticsService.trackHeapEvent({
            event,
            identity,
            user_id,
            properties: {
                ...properties,
                clearUpgradeUxVariant: this.clearUpgradeUxVariant,
                ...extraProperties,
            },
        });
    }
}
__decorate([
    observable
], PatientConsentClearUpgradeViewModel.prototype, "isSandboxLogin", void 0);
__decorate([
    observable
], PatientConsentClearUpgradeViewModel.prototype, "hasClearError", void 0);
__decorate([
    observable
], PatientConsentClearUpgradeViewModel.prototype, "isCLEARInformationShown", void 0);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "clearUpgradeUxVariant", null);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "viewToDisplay", null);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "flipLongPressLoginButton", null);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "sharedWithName", null);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "patientEmail", null);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "isLoggingIn", null);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "isClearLoginSuccess", null);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "verifyWithClearButtonText", null);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "verifyWithClearLongPressLabel", null);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "searchManuallyButtonText", null);
